import { Head, Link, router, usePage } from '@inertiajs/react'
import { useState } from 'react'
import { User, SharedProps } from '../../types'
import UserAvatar from '../../components/shared/UserAvatar'
import Layout from '../../components/Layout'

interface Props {
  pending_email: string | null
  pending_email_sent_at: string | null
}

export default function ProfileShow({ pending_email, pending_email_sent_at }: Props) {
  const { current_user } = usePage<{ props: SharedProps }>().props

  if (!current_user) return null

  const [form, setForm] = useState({
    first_name: current_user.first_name,
    last_name: current_user.last_name,
    title: current_user.title || '',
    phone: current_user.phone || '',
    avatar_text: current_user.avatar_text || '',
    avatar_url: current_user.avatar_url || '',
    avatar_size: current_user.avatar_size || 'md',
  })
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [phoneError, setPhoneError] = useState<string | null>(null)

  const validatePhone = (value: string): string | null => {
    if (!value) return null
    if (!/^[\d\s\+\-\.\(\)]+$/.test(value)) {
      return 'Phone must contain only digits, spaces, and common separators (+, -, ., parentheses)'
    }
    const digitsOnly = value.replace(/\D/g, '')
    if (digitsOnly.length < 7) {
      return 'Phone number must have at least 7 digits'
    }
    if (value.length > 25) {
      return 'Phone number must be 25 characters or fewer'
    }
    return null
  }

  const [showEmailChange, setShowEmailChange] = useState(false)
  const [newEmail, setNewEmail] = useState('')
  const [emailChanging, setEmailChanging] = useState(false)
  const [cancelling, setCancelling] = useState(false)

  const previewUser = {
    ...current_user,
    first_name: form.first_name,
    last_name: form.last_name,
    full_name: `${form.first_name} ${form.last_name}`,
    avatar_text: form.avatar_text || null,
    avatar_url: form.avatar_url || null,
    avatar_size: form.avatar_size as User['avatar_size'],
    avatar_display: form.avatar_text || `${form.first_name?.charAt(0) || ''}${form.last_name?.charAt(0) || ''}`.toUpperCase(),
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const error = validatePhone(form.phone)
    setPhoneError(error)
    if (error) return
    setSaving(true)
    setSaved(false)
    router.put('/profile', {
      user: {
        first_name: form.first_name,
        last_name: form.last_name,
        title: form.title || null,
        phone: form.phone || null,
        avatar_text: form.avatar_text || null,
        avatar_url: form.avatar_url || null,
        avatar_size: form.avatar_size,
      }
    }, {
      onFinish: () => {
        setSaving(false)
        setSaved(true)
        setTimeout(() => setSaved(false), 3000)
      }
    })
  }

  const handleEmailChangeRequest = (e: React.FormEvent) => {
    e.preventDefault()
    setEmailChanging(true)
    router.post('/profile/request_email_change', {
      user: { email: newEmail }
    }, {
      onFinish: () => {
        setEmailChanging(false)
        setNewEmail('')
        setShowEmailChange(false)
      }
    })
  }

  const handleCancelEmailChange = () => {
    setCancelling(true)
    router.delete('/profile/cancel_email_change', {
      onFinish: () => setCancelling(false)
    })
  }

  return (
    <Layout>
      <Head title="Profile - Pinnacle" />

      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-text-primary font-heading">Your Profile</h1>
          <p className="text-lg text-text-secondary mt-1">Manage your personal information and avatar settings.</p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="bg-white rounded-xl shadow-card p-6 mb-6">
            <h2 className="text-xl font-semibold text-text-primary font-heading mb-6 pb-4 border-b border-stone/10">
              Avatar Preview
            </h2>
            <div className="flex items-center gap-6 mb-6">
              <div className="flex flex-col items-center gap-2">
                <UserAvatar user={previewUser} size="xl" />
                <span className="text-sm text-text-muted">XL</span>
              </div>
              <div className="flex flex-col items-center gap-2">
                <UserAvatar user={previewUser} size="lg" />
                <span className="text-sm text-text-muted">Large</span>
              </div>
              <div className="flex flex-col items-center gap-2">
                <UserAvatar user={previewUser} size="md" />
                <span className="text-sm text-text-muted">Medium</span>
              </div>
              <div className="flex flex-col items-center gap-2">
                <UserAvatar user={previewUser} size="sm" />
                <span className="text-sm text-text-muted">Small</span>
              </div>
              <div className="flex flex-col items-center gap-2">
                <UserAvatar user={previewUser} size="xs" />
                <span className="text-sm text-text-muted">XS</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-card p-6 mb-6">
            <h2 className="text-xl font-semibold text-text-primary font-heading mb-6 pb-4 border-b border-stone/10">
              Personal Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1.5">First Name</label>
                <input
                  type="text"
                  value={form.first_name}
                  onChange={e => setForm(prev => ({ ...prev, first_name: e.target.value }))}
                  className="w-full px-3 py-2 border border-stone/30 rounded-lg text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/20 focus:border-pinnacle-red"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1.5">Last Name</label>
                <input
                  type="text"
                  value={form.last_name}
                  onChange={e => setForm(prev => ({ ...prev, last_name: e.target.value }))}
                  className="w-full px-3 py-2 border border-stone/30 rounded-lg text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/20 focus:border-pinnacle-red"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1.5">Title</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={e => setForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., VP of Sales"
                  className="w-full px-3 py-2 border border-stone/30 rounded-lg text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/20 focus:border-pinnacle-red"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1.5">Phone</label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={e => {
                    setForm(prev => ({ ...prev, phone: e.target.value }))
                    if (phoneError) setPhoneError(validatePhone(e.target.value))
                  }}
                  onBlur={() => setPhoneError(validatePhone(form.phone))}
                  placeholder="e.g., (555) 123-4567"
                  className={`w-full px-3 py-2 border rounded-lg text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/20 focus:border-pinnacle-red ${phoneError ? 'border-red-500' : 'border-stone/30'}`}
                />
                {phoneError && (
                  <p className="text-sm text-red-600 mt-1">{phoneError}</p>
                )}
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-text-primary mb-1.5">Email</label>
                <div className="flex items-center gap-3">
                  <input
                    type="email"
                    value={current_user.email}
                    disabled
                    className="flex-1 px-3 py-2 border border-stone/20 rounded-lg text-sm text-text-secondary bg-[#F5F5F5]/20"
                  />
                  {!pending_email && !showEmailChange && (
                    <button
                      type="button"
                      onClick={() => setShowEmailChange(true)}
                      className="text-sm font-medium text-pinnacle-red hover:text-pinnacle-red/80 whitespace-nowrap transition-colors"
                    >
                      Change email
                    </button>
                  )}
                </div>

                {pending_email && (
                  <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <svg className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-amber-800">
                          Pending email change to <strong>{pending_email}</strong>
                        </p>
                        <p className="text-sm text-amber-700 mt-0.5">
                          A verification link was sent to that address.
                          {pending_email_sent_at && (
                            <> Check your inbox and confirm within 24 hours.</>
                          )}
                        </p>
                        <button
                          type="button"
                          onClick={handleCancelEmailChange}
                          disabled={cancelling}
                          className="mt-2 text-sm font-medium text-amber-800 hover:text-amber-900 underline underline-offset-2 disabled:opacity-50"
                        >
                          {cancelling ? 'Cancelling...' : 'Cancel this request'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {showEmailChange && !pending_email && (
                  <div className="mt-3 p-4 bg-[#F5F5F5]/40 border border-stone/20 rounded-lg">
                    <p className="text-sm text-text-muted mb-3">
                      Enter your new email address. We will send a verification link to confirm the change.
                    </p>
                    <div className="flex items-end gap-3">
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-text-primary mb-1.5">New email address</label>
                        <input
                          type="email"
                          value={newEmail}
                          onChange={e => setNewEmail(e.target.value)}
                          placeholder="new@example.com"
                          className="w-full px-3 py-2 border border-stone/30 rounded-lg text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/20 focus:border-pinnacle-red"
                          required
                        />
                      </div>
                      <button
                        type="button"
                        onClick={handleEmailChangeRequest}
                        disabled={emailChanging || !newEmail}
                        className="bg-pinnacle-red hover:bg-pinnacle-red/90 disabled:opacity-50 text-white font-semibold py-2 px-4 rounded-lg transition-colors text-sm whitespace-nowrap"
                      >
                        {emailChanging ? 'Sending...' : 'Send verification'}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setShowEmailChange(false); setNewEmail('') }}
                        className="text-sm text-text-muted hover:text-text-primary py-2 px-3 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                    <p className="text-xs text-text-muted mt-2">
                      Your email will only change after you confirm the verification link. A notification will also be sent to your current email.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-card p-6 mb-6">
            <h2 className="text-xl font-semibold text-text-primary font-heading mb-6 pb-4 border-b border-stone/10">
              Avatar Settings
            </h2>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1.5">
                  Custom Avatar Text
                </label>
                <input
                  type="text"
                  value={form.avatar_text}
                  onChange={e => setForm(prev => ({ ...prev, avatar_text: e.target.value.slice(0, 3) }))}
                  maxLength={3}
                  placeholder="e.g., AM, CEO"
                  className="w-32 px-3 py-2 border border-stone/30 rounded-lg text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/20 focus:border-pinnacle-red text-center"
                />
                <p className="text-sm text-text-muted mt-1">Up to 3 characters. Leave empty to use your initials ({current_user.initials}).</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-1.5">
                  Profile Picture URL
                </label>
                <input
                  type="url"
                  value={form.avatar_url}
                  onChange={e => setForm(prev => ({ ...prev, avatar_url: e.target.value }))}
                  placeholder="https://example.com/your-photo.jpg"
                  className="w-full px-3 py-2 border border-stone/30 rounded-lg text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/20 focus:border-pinnacle-red"
                />
                <p className="text-sm text-text-muted mt-1">Paste a URL to your profile picture. This overrides the text avatar.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-3">
                  Default Avatar Size
                </label>
                <div className="flex gap-4">
                  {[
                    { value: 'sm', label: 'Small' },
                    { value: 'md', label: 'Medium' },
                    { value: 'lg', label: 'Large' },
                  ].map(opt => (
                    <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="avatar_size"
                        value={opt.value}
                        checked={form.avatar_size === opt.value}
                        onChange={e => setForm(prev => ({ ...prev, avatar_size: e.target.value }))}
                        className="w-4 h-4 text-pinnacle-red focus:ring-pinnacle-red/20 border-stone/40"
                      />
                      <span className="text-sm text-text-primary">{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <Link href="/" className="text-base text-text-secondary hover:text-text-primary">
              Back to Dashboard
            </Link>
            <div className="flex items-center gap-3">
              {saved && (
                <span className="text-base text-[#15532E] font-medium flex items-center gap-1.5">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Profile saved
                </span>
              )}
              <button
                type="submit"
                disabled={saving}
                className="bg-pinnacle-red hover:bg-pinnacle-red/90 disabled:opacity-50 text-white font-semibold py-3 px-6 rounded-lg transition-colors font-heading text-base"
              >
                {saving ? 'Saving...' : 'Save Profile'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </Layout>
  )
}
